/**
 * Share Revocation Handler - Recovery logic for revoked shares
 *
 * This module handles the recovery process when a share is revoked by its owner.
 * Creates a recovered standalone document from the snapshot + local data.
 *
 * Extracted from crdt-bridge.js for better separation of concerns.
 * Uses a callback injection pattern to avoid circular dependencies.
 *
 * Recovery strategy:
 * 1. Fetch server snapshot (owner's state at revocation time)
 * 2. Merge with local CRDT data (if available)
 * 3. Create recovered standalone map
 * 4. Confirm recovery to server
 */

import { backupAttachmentReferences, remapBackupLinks } from '../crypto/backup-state.js';
import { persistRecoveryCopy } from '../crypto/data-export.js';
import { recordQuotaRejection } from '../services/quota-state.js';
import { emit, Events } from "../core/events.js";
import { createLogger } from "../core/logger.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import { E2EE } from "../crypto/e2ee.js";
import { rekeyBek, rekeyFilename } from "../crypto/attachment-crypto.js";
import {
  listAttachments,
  base64ToUint8,
  uint8ToBase64,
} from "../services/attachment-service.js";
import { notify } from "../host.js";
import { NOTICE } from "../notices.js";
import { decompress } from "./compression.js";
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { nodeCountOf, readTree, flatNodeToTree } from "./map-format.js";
import { generateUUID } from "../core/uuid.js";
import {
  getActiveShareId,
} from "./share-state.js";
import {
  stopSharedDocSync,
  getSharedDocManager,
} from "./share-lifecycle.js";
import { getDocState, saveDocState } from "../../shared/local-doc-state.js";
import { deleteDocCache } from "../storage/index.js";
import { t } from "../../shared/i18n/index.js";

// ============================================
// Logging Setup
// ============================================
const log = createLogger("Share Revocation");

const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

// ============================================
// Module State
// ============================================

/** Track shares already revoked to prevent duplicates (capped to prevent unbounded growth) */
let processedRevocations = new Set();
const MAX_PROCESSED_REVOCATIONS = 500;

/** Cache DEKs for recovery (survives VaultStorage removal during race conditions) */
const _recoveryDEKStore = new Map(); // shareId -> { key, timestamp }
const RECOVERY_DEK_TTL = 5 * 60 * 1000; // 5 minutes

function _cleanExpiredDEKs() {
  const now = Date.now();
  for (const [id, entry] of _recoveryDEKStore) {
    if (now - entry.timestamp > RECOVERY_DEK_TTL) {
      // Zero key material before deleting
      if (entry.key instanceof Uint8Array) entry.key.fill(0);
      _recoveryDEKStore.delete(id);
    }
  }
}

// Periodic cleanup every 60 seconds
let _cleanupIntervalId = setInterval(_cleanExpiredDEKs, 60 * 1000);

/** TTL-aware wrapper around the DEK cache */
const recoveryDEKCache = {
  has(id) {
    const entry = _recoveryDEKStore.get(id);
    if (!entry) return false;
    if (Date.now() - entry.timestamp > RECOVERY_DEK_TTL) {
      if (entry.key instanceof Uint8Array) entry.key.fill(0);
      _recoveryDEKStore.delete(id);
      return false;
    }
    return true;
  },
  get(id) {
    const entry = _recoveryDEKStore.get(id);
    if (!entry) return undefined;
    if (Date.now() - entry.timestamp > RECOVERY_DEK_TTL) {
      if (entry.key instanceof Uint8Array) entry.key.fill(0);
      _recoveryDEKStore.delete(id);
      return undefined;
    }
    return entry.key;
  },
  set(id, key) {
    _recoveryDEKStore.set(id, { key, timestamp: Date.now() });
  },
  delete(id) {
    const entry = _recoveryDEKStore.get(id);
    if (entry?.key instanceof Uint8Array) entry.key.fill(0);
    _recoveryDEKStore.delete(id);
  },
  clear() {
    for (const entry of _recoveryDEKStore.values()) {
      if (entry?.key instanceof Uint8Array) entry.key.fill(0);
    }
    _recoveryDEKStore.clear();
  },
};

// ============================================
// Callback Injection Pattern
// ============================================

/**
 * Callbacks provided by crdt-bridge.js during initialization
 * @type {Object}
 */
let callbacks = {
  getDocManager: null,           // () => docManager (main doc manager)
  getStore: null,                // () => store module
  getSyncingDepth: null,         // () => syncingDepth
  incrementSyncingDepth: null,   // () => void
  decrementSyncingDepth: null,   // () => void
  disconnectMainSync: null,      // () => Promise<void> — disconnect main sync manager
};

/**
 * Initialize share revocation handler with callbacks from crdt-bridge.js
 * @param {Object} cbs - Callback functions
 */
export function initShareRevocation(cbs) {
  callbacks = { ...callbacks, ...cbs };
  logDebug("Share revocation handler initialized");
}

/**
 * Check if a share revocation is already in progress or completed for this session.
 * Used by notifications.js to avoid showing a "Recover" notification for shares
 * that are already being handled via the active-doc WS path.
 * @param {string} shareId
 * @returns {boolean}
 */
export function isRevocationProcessed(shareId) {
  return processedRevocations.has(shareId);
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique node ID
 * @returns {string} UUID
 */
function generateNodeId() {
  return generateUUID();
}

/**
 * Recursively regenerate IDs for a node tree.
 * Used when recovering shared documents to make nodes truly independent.
 *
 * @param {Object} node - The node to process
 * @returns {Object|null} The processed node (for chaining), or null if input was null
 */
export function regenerateIdsAndStripFlags(node) {
  if (!node) return null;

  // Generate new ID to prevent conflicts with original shared nodes
  node.id = generateNodeId();

  // Process children recursively
  if (node.children) {
    node.children.forEach(regenerateIdsAndStripFlags);
  }

  return node;
}

// ============================================
// Attachment migration during recovery
// ============================================

/** Rewrite every `attachment:<fromDocId>/<blobId>` URI to point at `toDocId`
 *  inside a markdown string. Case-insensitive match on the doc id. */
function rewriteAttachmentRefsInString(str, fromDocId, toDocId) {
  if (typeof str !== "string" || !str.includes("attachment:")) return str;
  const escaped = fromDocId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`attachment:${escaped}/`, "gi");
  return str.replace(re, `attachment:${toDocId}/`);
}

/** Walk a tree mutating any attachment URIs that pointed at `fromDocId`
 *  so they point at `toDocId`. Tolerates both string-form notes (per node)
 *  and array-form doc-level notes (`{content}` records). */
function rewriteAttachmentRefsInTree(tree, fromDocId, toDocId) {
  if (!tree) return;
  if (typeof tree.notes === "string") {
    tree.notes = rewriteAttachmentRefsInString(tree.notes, fromDocId, toDocId);
  } else if (Array.isArray(tree.notes)) {
    for (const n of tree.notes) {
      if (n && typeof n.content === "string") {
        n.content = rewriteAttachmentRefsInString(n.content, fromDocId, toDocId);
      }
    }
  }
  (tree.children || []).forEach((c) => rewriteAttachmentRefsInTree(c, fromDocId, toDocId));
}

/** Collect every blob id referenced by `attachment:<sourceDocId>/<blobId>` in
 *  the tree's notes. Other doc ids (e.g. cross-doc refs) are ignored — those
 *  blobs aren't ours to migrate. */
function collectBlobIdsFromTreeForDoc(tree, sourceDocId) {
  const ids = new Set();
  const escaped = sourceDocId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`attachment:${escaped}/([a-f0-9-]+)`, "gi");
  const scan = (str) => {
    if (typeof str !== "string" || !str.includes("attachment:")) return;
    for (const m of str.matchAll(re)) ids.add(m[1].toLowerCase());
  };
  const visit = (node) => {
    if (!node) return;
    if (typeof node.notes === "string") {
      scan(node.notes);
    } else if (Array.isArray(node.notes)) {
      for (const n of node.notes) scan(n?.content);
    }
    (node.children || []).forEach(visit);
  };
  visit(tree);
  return [...ids];
}

/** Wrap a raw 32-byte DEK (Uint8Array) as a non-extractable AES-GCM CryptoKey
 *  for use with rekeyBek/rekeyFilename. */
async function importDekKey(rawBytes) {
  return crypto.subtle.importKey(
    "raw",
    rawBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Migrate the recovered tree's attachment refs from `sourceDocId` to
 * `destDocId`. Each ref's BEK is rewrapped under the new doc's DEK, the
 * filename is re-encrypted, and a new `doc_blob_refs` row is inserted on the
 * server pointing at the same underlying file. The tree's URIs are rewritten
 * in place.
 *
 * Reads PRE-rotation wrappings from `/api/shares/:id/snapshot/blob-refs`
 * (which the server populates inside the revoking-rotation transaction).
 * The live `doc_blob_refs` rows have already been rewrapped under the new
 * (post-rotation) DEK that the revoked user doesn't have, so calling
 * `listAttachments(sourceDocId)` would yield wrappings we can't unwrap. The
 * snapshot endpoint returns the frozen pre-rotation values keyed to the old
 * DEK (which the user still has cached in vault).
 *
 * Falls back to `listAttachments(sourceDocId)` for legacy snapshots that
 * predate the wrapped_bek snapshot table (revocations performed before
 * migration 20260521000001). Those recoveries will still fail per-blob, but
 * the fallback at least preserves whatever the previous behavior was.
 *
 * Returns partial results on failure. The caller must retain the source and
 * leave recovery unconfirmed until every reference has been copied.
 *
 * Must run BEFORE confirmRecovery: the import endpoint requires an in-flight
 * recovery claim on the source doc, which `confirmRecovery` closes.
 *
 * @param {string} sourceDocId - The original (revoked) doc.
 * @param {string} destDocId   - The newly created recovered doc.
 * @param {Uint8Array} oldDekRaw - DEK of the source doc (from the snapshot).
 * @param {Uint8Array} newDekRaw - DEK of the destination doc.
 * @param {Object} treeData    - Recovered tree (mutated in place).
 * @returns {Promise<{imported: number, total: number}>}
 */
export async function migrateAttachmentsForRecovery(
  sourceDocId,
  destDocId,
  oldDekRaw,
  newDekRaw,
  treeData,
  extraBlobIds = [],
) {
  const blobIds = new Set([...collectBlobIdsFromTreeForDoc(treeData, sourceDocId), ...extraBlobIds]);
  if (blobIds.size === 0) {
    logDebug("No attachments to migrate during recovery");
    return { imported: 0, total: 0 };
  }
  logInfo(`Migrating ${blobIds.size} attachment ref(s) for recovery`);

  let oldDek;
  let newDek;
  try {
    oldDek = await importDekKey(oldDekRaw);
    newDek = await importDekKey(newDekRaw);
  } catch (e) {
    logError("Failed to import DEKs for blob migration:", e);
    return { imported: 0, total: blobIds.size };
  }

  // Prefer the snapshot-blob-refs endpoint: its rows are the PRE-rotation
  // wrappings, which match `oldDek`. Fall back to `listAttachments` only
  // for legacy snapshots that predate this table (pre-rotation snapshots
  // captured before migration 20260521000001 don't have rows here — the
  // best the fallback can do is the broken-as-before behavior).
  let attachments = null;
  let usedFallback = false;
  try {
    const resp = await fetch(`/api/shares/${sourceDocId}/snapshot/blob-refs`, {
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (resp.ok) {
      const data = await resp.json();
      attachments = (data?.items || []).map((it) => ({
        attachmentId: it.blob_id,
        wrappedBek: it.wrapped_bek,
        filenameEncrypted: it.filename_encrypted || null,
      }));
    } else if (resp.status !== 404) {
      logWarn("snapshot/blob-refs returned", resp.status, "- falling back to listAttachments");
    }
  } catch (e) {
    logWarn("snapshot/blob-refs fetch failed, falling back to listAttachments:", e);
  }

  if (!attachments || attachments.length === 0) {
    // Either no snapshot rows (legacy) or fetch failed. Fall back to the
    // live listing — for legacy snapshots this preserves prior behavior;
    // for fresh ones with empty doc_blob_refs there's nothing to migrate.
    try {
      attachments = await listAttachments(sourceDocId);
      if (attachments.length > 0) usedFallback = true;
    } catch (e) {
      logError("Failed to list source-doc attachments for migration:", e);
      return { imported: 0, total: blobIds.size };
    }
  }

  if (usedFallback) {
    logWarn(
      `Recovery using legacy listAttachments path for ${sourceDocId} — ` +
      `wrapped_beks may be under post-rotation DEK and fail to rewrap. ` +
      `This affects revocations created before migration 20260521000001.`,
    );
  }

  // Build the rewrapped batch, skipping any blob that's no longer in the
  // source doc (race with sweep) or fails to rewrap (legacy/malformed).
  const items = [];
  for (const a of attachments) {
    if (!blobIds.has(a.attachmentId.toLowerCase())) continue;
    try {
      const oldWrapped = base64ToUint8(a.wrappedBek);
      const newWrapped = await rekeyBek(oldDek, newDek, oldWrapped);

      let newFilenameEncrypted = null;
      if (a.filenameEncrypted) {
        const oldFn = base64ToUint8(a.filenameEncrypted);
        const rewrapped = await rekeyFilename(oldDek, newDek, oldFn);
        if (rewrapped) {
          newFilenameEncrypted = uint8ToBase64(rewrapped);
        }
      }

      items.push({
        blob_id: a.attachmentId,
        wrapped_bek: uint8ToBase64(newWrapped),
        filename_encrypted: newFilenameEncrypted,
      });
    } catch (e) {
      logWarn(`Failed to rewrap attachment ${a.attachmentId}:`, e);
    }
  }

  if (items.length === 0) {
    return { imported: 0, total: blobIds.size };
  }

  // POST the rewrapped batch — server inserts doc_blob_refs rows pointing at
  // the existing blob_files. Caller must own destDocId and have an active
  // recovery claim on sourceDocId; the handler enforces both.
  let imported = 0;
  try {
    const response = await fetch(`/api/docs/${destDocId}/blob-refs/import`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ source_doc_id: sourceDocId, items }),
    });
    if (!response.ok) {
      if (response.status === 413) recordQuotaRejection(destDocId);
      logError("Blob-refs import failed:", response.status);
      return { imported: 0, total: blobIds.size };
    }
    const data = await response.json().catch(() => null);
    imported = data?.imported ?? items.length;
  } catch (e) {
    logError("Blob-refs import network error:", e);
    return { imported: 0, total: blobIds.size };
  }

  // Rewrite URIs in the tree so the recovered doc points at its own refs.
  rewriteAttachmentRefsInTree(treeData, sourceDocId, destDocId);

  logInfo(`Migrated ${imported}/${blobIds.size} attachment refs into recovered doc`);
  return { imported, total: blobIds.size };
}

// ============================================
// Snapshot Recovery API Functions
// ============================================

/**
 * Fetch the recovery snapshot for a revoked share from the server
 * @param {string} shareId - The share ID
 * @returns {Promise<{encrypted_state: string, snapshot_id: string} | null>}
 */
export async function fetchRevocationSnapshot(shareId) {
  try {
    const response = await fetch(`/api/shares/${shareId}/snapshot`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      if (response.status === 404) {
        logDebug("No snapshot available for share:", shareId);
        return null;
      }
      throw new Error(`Failed to fetch snapshot: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logError("Error fetching snapshot:", error);
    return null;
  }
}

/**
 * Claim recovery for a revoked share (cross-device dedup).
 * Only one device per user gets the claim.
 * @param {string} shareId
 * @returns {Promise<{claimed: boolean}>}
 */
export async function claimRecovery(shareId) {
  try {
    const response = await fetch(`/api/shares/${shareId}/claim-recovery`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    });
    if (!response.ok) {
      logWarn("Failed to claim recovery:", response.status);
      return { claimed: false };
    }
    return await response.json();
  } catch (error) {
    logError("Error claiming recovery:", error);
    return { claimed: false };
  }
}

/**
 * Confirm recovery to the server after successfully recovering data
 * @param {string} shareId - The share ID
 * @param {string} recoveredDocId - Optional ID of the recovered document
 * @returns {Promise<boolean>}
 */
export async function confirmRecovery(shareId, recoveredDocId = null) {
  try {
    const response = await fetch(`/api/shares/${shareId}/confirm-recovery`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ recovered_doc_id: recoveredDocId }),
    });

    if (!response.ok) {
      logWarn("Failed to confirm recovery:", response.status);
      return false;
    }

    const data = await response.json();
    logInfo("Recovery confirmed:", data);
    return data.success;
  } catch (error) {
    logError("Error confirming recovery:", error);
    return false;
  }
}

/**
 * M11: Confirm recovery with retry logic
 * @param {string} shareId - The share ID
 * @param {string} recoveredDocId - Optional ID of the recovered document
 * @param {number} attempts - Number of retry attempts (default 3)
 */
export async function confirmRecoveryWithRetry(shareId, recoveredDocId = null, attempts = 3) {
  for (let i = 0; i < attempts; i++) {
    try {
      const success = await confirmRecovery(shareId, recoveredDocId);
      if (success) {
        logInfo("Recovery confirmed for share:", shareId);
        return;
      }
      // success=false means the atomic claim expired (another device won the race).
      // The local recovered map is valid user data — do NOT delete it.
      // The other device's recovery takes precedence on the server side only.
      logWarn("Recovery claim expired; another device handled recovery. Keeping local recovered map:", recoveredDocId);
      return;
    } catch (err) {
      logWarn(`Recovery confirmation attempt ${i + 1} failed:`, err);
    }
    if (i < attempts - 1) {
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
  logError("Failed to confirm recovery after all attempts:", shareId);

  emit(Events.SYNC_ERROR, {
    type: 'recovery_confirmation_failed',
    shareId,
    message: t('share.recovery.confirmFailed'),
  });
}

/**
 * Decrypt and apply server snapshot to a Yjs document
 * @param {string} encryptedStateBase64 - Base64 encoded encrypted state
 * @param {Uint8Array} dek - The share's decryption key
 * @param {Y.Doc} targetDoc - The Yjs document to apply the update to
 * @param {string} [shareId] - Optional share ID for AAD verification
 * @returns {Promise<boolean>}
 */
export async function decryptAndApplySnapshot(encryptedStateBase64, dek, targetDoc, shareId = null) {
  try {
    // Decode base64
    const encryptedState = Uint8Array.from(atob(encryptedStateBase64), c => c.charCodeAt(0));

    // Decrypt using share DEK (with AAD if shareId provided, falls back to legacy)
    const decryptedState = await E2EE.decrypt(dek, encryptedState, shareId);

    // Decompress if gzipped (backward compatible with uncompressed data)
    const state = await decompress(decryptedState);

    // Apply to Yjs doc
    Y.applyUpdate(targetDoc, state);

    logDebug("Successfully applied server snapshot to doc");
    return true;
  } catch (error) {
    logError("Failed to decrypt/apply snapshot:", error);
    return false;
  }
}

/**
 * Get the DEK for a share (checks recovery cache first, then VaultStorage)
 * @param {string} shareId - The share ID
 * @returns {Promise<Uint8Array | null>}
 */
export async function getShareDEK(shareId) {
  try {
    // Check recovery cache first (survives VaultStorage removal during recovery race conditions)
    if (recoveryDEKCache.has(shareId)) {
      logInfo("Using cached DEK from recovery cache:", shareId);
      return recoveryDEKCache.get(shareId);
    }

    const dek = await VaultStorage.getDocDEK(shareId);
    return dek;
  } catch (error) {
    logError("Failed to get share DEK:", error);
    return null;
  }
}

/**
 * Merge server snapshot with local data for recovery
 * Creates a temporary Yjs doc, merges both states, and returns the resulting tree
 *
 * @param {string} shareId - The share ID
 * @param {Object} snapshotData - The snapshot data from server {encrypted_state, snapshot_id}
 * @param {Object|null} localTreeData - Local tree data (from memory or IndexedDB)
 * @returns {Promise<{treeData: Object, mergeSource: string} | null>}
 */
export async function mergeSnapshotWithLocal(shareId, snapshotData, localTreeData) {
  const dek = await getShareDEK(shareId);
  if (!dek) {
    logWarn("Cannot merge without DEK, using local only");
    return localTreeData ? { treeData: localTreeData, mergeSource: "local" } : null;
  }

  // Create a temporary doc for merging
  const mergeDoc = new Y.Doc();

  // If we have local data, first try to apply local updates to the merge doc
  let hasLocalData = false;
  const mainManager = callbacks.getDocManager?.();
  const sharedDocManager = getSharedDocManager(shareId) || (mainManager?.docId === shareId ? mainManager : null);
  if (sharedDocManager) {
    // Get the full state from the in-memory doc
    const localState = Y.encodeStateAsUpdate(sharedDocManager.doc);
    Y.applyUpdate(mergeDoc, localState);
    hasLocalData = true;
    logDebug("Applied local in-memory state to merge doc");
  } else {
    // Try IndexedDB
    let persistence;
    try {
      const tempDoc = new Y.Doc();
      persistence = new IndexeddbPersistence(`orbital-${shareId}`, tempDoc);
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error("Timeout")), 5000);
        persistence.once("synced", () => {
          clearTimeout(timeout);
          resolve();
        });
      });

      if (nodeCountOf(tempDoc) > 0) {
        const localState = Y.encodeStateAsUpdate(tempDoc);
        Y.applyUpdate(mergeDoc, localState);
        hasLocalData = true;
        logDebug("Applied IndexedDB state to merge doc");
      }
      persistence.destroy();
      persistence = null;
    } catch (e) {
      logDebug("No IndexedDB cache available");
      if (persistence) {
        try { persistence.destroy(); } catch (_) { /* best-effort cleanup */ }
      }
    }
  }

  // Apply the server snapshot (this will merge with local due to CRDT)
  const applied = snapshotData ? await decryptAndApplySnapshot(snapshotData.encrypted_state, dek, mergeDoc, shareId) : false;
  if (!applied && !hasLocalData) {
    logWarn("No data available for recovery");
    return null;
  }

  // Convert merged doc to tree (either map schema: engine/sync/map-format.js)
  const nodeCount = nodeCountOf(mergeDoc);
  if (nodeCount === 0) {
    logWarn("Merged doc has no nodes");
    return localTreeData ? { treeData: localTreeData, mergeSource: "local" } : null;
  }

  if (!mergeDoc.getMap("metadata").get("rootId")) {
    logWarn("Merged doc has no rootId");
    return localTreeData ? { treeData: localTreeData, mergeSource: "local" } : null;
  }

  const mergedTree = readTree(mergeDoc, flatNodeToTree);
  const mergeSource = applied ? (hasLocalData ? "merged" : "server") : "local";

  logInfo(`Recovery merge complete: source=${mergeSource}, nodes=${nodeCount}`);
  const crdtState = Y.encodeStateAsUpdate(mergeDoc);
  mergeDoc.destroy();
  return { treeData: mergedTree, mergeSource, crdtState, complete: !snapshotData || applied };
}

/**
 * Load tree data from IndexedDB cache
 * Used for recovery when user never opened the share (pre-cached on invite accept)
 */
export async function loadTreeDataFromIndexedDB(shareId) {
  try {
    const tempDoc = new Y.Doc();
    const persistence = new IndexeddbPersistence(`orbital-${shareId}`, tempDoc);

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Timeout loading cached data"));
      }, 5000);

      persistence.once("synced", () => {
        clearTimeout(timeout);
        resolve();
      });
    });

    if (nodeCountOf(tempDoc) === 0 || !tempDoc.getMap("metadata").get("rootId")) {
      persistence.destroy();
      return null;
    }

    // Either map schema (engine/sync/map-format.js)
    const treeData = readTree(tempDoc, flatNodeToTree);
    persistence.destroy();

    return treeData?.id ? treeData : null;
  } catch (error) {
    logWarn("Failed to load from IndexedDB:", error);
    return null;
  }
}

// ============================================
// Main Revocation Handler
// ============================================

/**
 * Consolidated share revocation handler
 * Called from both WebSocket (handleShareRevoked) and notification polling (vault.js event)
 *
 * @param {string} shareId - The share ID being revoked
 * @param {string} titleHint - Optional title hint from notification/websocket
 * @param {Object} options - Options: { fromWebSocket, fromNotification }
 * @returns {Promise<{success: boolean, recoveredDocId?: string}>}
 */
export async function performShareRevocation(shareId, titleHint, options = {}) {
  logInfo("Performing share revocation:", shareId, options);

  // Prevent duplicate processing (WebSocket and notification can both trigger)
  if (processedRevocations.has(shareId)) {
    // If the share still exists in vault, the previous recovery didn't fully clean up
    // (e.g. re-shared then re-revoked). Allow re-processing.
    const stillInVault = VaultStorage.getDoc?.(shareId);
    if (!stillInVault) {
      logDebug("Share already revoked and cleaned up, skipping:", shareId);
      return { success: true, alreadyProcessed: true };
    }
    logInfo("Share previously processed but still in vault, retrying:", shareId);
    processedRevocations.delete(shareId);
  }
  // Cap the set to prevent unbounded growth in long-running sessions
  if (processedRevocations.size >= MAX_PROCESSED_REVOCATIONS) {
    const oldest = processedRevocations.values().next().value;
    processedRevocations.delete(oldest);
  }
  processedRevocations.add(shareId);

  // Get share info SYNCHRONOUSLY before any async work
  const shareInfo = VaultStorage.getDoc?.(shareId);

  // Check if this share is currently being viewed. Two paths:
  // 1. Shared doc: activeShareId === shareId
  // 2. Main doc opened via handleDocOpen: docManager.docId === shareId
  let wasActive = getActiveShareId() === shareId;
  if (!wasActive && callbacks.getDocManager) {
    const mainMgr = callbacks.getDocManager();
    if (mainMgr && mainMgr.docId === shareId) {
      wasActive = true;
    }
  }
  const title = titleHint || shareInfo?.title || t("share.recovery.defaultTitle");

  // Dismiss notification IMMEDIATELY (before any async work) so that if a
  // share_revoked notification arrives on the user WS during DEK caching or
  // recovery, the guard in notifications.js (_alreadyRevokedShares) is already set.
  if (wasActive || options.fromWebSocket) {
    emit(Events.DISMISS_SHARE_NOTIFICATION, { shareId });
  }

  // Cache the DEK before any async work that might remove the share from VaultStorage
  if (shareInfo?.wrappedDEK && !recoveryDEKCache.has(shareId)) {
    try {
      const dek = await getShareDEK(shareId);
      if (dek) {
        recoveryDEKCache.set(shareId, dek);
        logDebug("Pre-cached DEK for recovery:", shareId);
      }
    } catch (e) {
      logWarn("Failed to pre-cache DEK:", e);
    }
  }

  // Check if we're the owner - owners don't need recovery.
  // This uses vault-local role data (shareInfo.role) rather than a server round-trip.
  // The vault is encrypted and is the trusted session source of truth for the user's
  // own role. An adversary who can tamper with the vault has already compromised the
  // session entirely, so a server re-check would not add meaningful protection here.
  const isOwner = shareInfo?.role === "owner";
  if (isOwner) {
    logInfo("Owner stopped sharing, no recovery needed");
    await stopSharedDocSync(shareId);
    await VaultStorage.deleteDoc(shareId);
    recoveryDEKCache.delete(shareId); // Clean up cache
    emit(Events.SHARE_REVOKED, { shareId, wasActive, isOwner: true });
    return { success: true };
  }

  // --- Recovery ---
  // Always attempt recovery. The claim mechanism is advisory (cross-device dedup)
  // and must never block recovery. A duplicate recovered doc on another device
  // is far better than losing data.

  // Capture in-memory CRDT state BEFORE stopping sync (avoids data loss
  // from IndexedDB write debounce window).
  // Check shared doc managers first, then fall back to main doc manager
  // (shared docs opened via handleDocOpen use the main doc manager, not sharedDocManagers).
  let preStopManager = getSharedDocManager(shareId);
  let mainSyncMatchesShare = false;
  if (!preStopManager && callbacks.getDocManager) {
    const mainMgr = callbacks.getDocManager();
    if (mainMgr && mainMgr.docId === shareId) {
      preStopManager = mainMgr;
      mainSyncMatchesShare = true;
    }
  }
  const preStopTreeData = preStopManager ? preStopManager.toTree() : null;

  await stopSharedDocSync(shareId);

  // Also stop the main sync manager if it's connected to the revoked share.
  // This happens when a shared doc is opened via handleDocOpen (uses main sync,
  // not sharedDocManagers). Without this, handleDekRotated's reconnect loop
  // continues indefinitely since the server rejects the revoked user.
  if (mainSyncMatchesShare && callbacks.disconnectMainSync) {
    callbacks.disconnectMainSync().catch(e => logWarn("Failed to disconnect main sync:", e));
  }

  // Claim recovery (cross-device dedup) and AWAIT it. The server-side
  // attachment-import and confirm-recovery endpoints both require
  // recovery_claimed_at IS NOT NULL — if we fire-and-forget and proceed too
  // fast, blob-refs/import and confirmRecovery race the claim insert and can
  // 403/404. We always proceed regardless of `claimed` (the recovered map is
  // valid user data even if another device won the claim race), but the
  // *server state* must be set before any downstream call.
  try {
    const claimResult = await claimRecovery(shareId);
    if (!claimResult?.claimed) {
      logInfo("claim-recovery: another device won the claim race; proceeding with local recovery anyway");
    }
  } catch (e) {
    logWarn("claim-recovery failed (proceeding anyway):", e);
  }

  // For active docs via WebSocket: defer recovery until user clicks OK in the modal.
  // This avoids multiple devices racing to create duplicate recovered docs.
  // The notification is already dismissed, so a second device won't also recover.
  if (wasActive && options.fromWebSocket) {
    logInfo("Active doc revoked — deferring recovery to user confirmation");
    emit(Events.SHARE_REVOCATION_READY, {
      shareId,
      title,
      preStopTreeData,
    });
    return { success: true, deferred: true };
  }

  return await performDocumentRevocation(
    shareId,
    title,
    wasActive,
    options,
    preStopTreeData,
  );
}

/**
 * Handle revocation for a shared document.
 * Creates a standalone copy preserving the full CRDT and its internal references.
 *
 * Recovery strategy:
 * 1. Try to fetch server snapshot (owner's state at revocation time)
 * 2. Merge server snapshot with local data (CRDT merge)
 * 3. Fall back to local-only if server snapshot unavailable
 * 4. Confirm recovery to server after success
 */
export async function performDocumentRevocation(
  shareId,
  title,
  wasActive,
  options,
  preStopTreeData = null,
) {
  logInfo("Revoking standalone share:", shareId);

  // Recovery replaces the map with a copy and deletes the original. That is
  // only right for maps shared WITH us; for our own map it would delete it.
  if (VaultStorage.getDoc?.(shareId)?.role === "owner") {
    throw new Error("Refusing to recover a map you own: your access was not revoked.");
  }

  let treeData = null;
  let recoverySource = null;
  let snapshotData = null;
  let crdtState = null, completeSource = true;

  // Step 1: Try to fetch server snapshot (before cleaning up local state)
  // The server snapshot contains the owner's state at revocation time
  snapshotData = await fetchRevocationSnapshot(shareId);
  if (snapshotData) {
    logDebug("Server snapshot available, will merge with local");
  }

  // Step 2: Get local data sources
  let localTreeData = null;

  // Use pre-stop in-memory data if available (captured before sync was stopped)
  if (preStopTreeData) {
    localTreeData = preStopTreeData;
    recoverySource = "memory";
    logDebug("Got tree data from pre-stop in-memory snapshot");
  }

  // Fallback to IndexedDB cache
  if (!localTreeData) {
    logDebug("Trying IndexedDB fallback for recovery");
    localTreeData = await loadTreeDataFromIndexedDB(shareId);
    if (localTreeData) {
      recoverySource = "indexeddb";
      logDebug("Got tree data from IndexedDB cache");
    }
  }

  // Step 3: Preserve the full CRDT, including note fragments and execution data.
  {
    const mergeResult = await mergeSnapshotWithLocal(shareId, snapshotData, localTreeData);
    if (mergeResult) {
      treeData = mergeResult.treeData;
      crdtState = mergeResult.crdtState || null;
      completeSource = !snapshotData || mergeResult.complete === true;
      recoverySource = mergeResult.mergeSource;
      logDebug(`Merged recovery data, source: ${recoverySource}`);
    }
  }

  // Fall back to local-only if merge failed or no snapshot
  if (!treeData && localTreeData) {
    treeData = localTreeData;
    recoverySource = recoverySource || "local";
  }

  if (!treeData) {
    logWarn("No data found for recovery:", shareId);
    // Keep VaultStorage entry so user can retry recovery on next login
    // Only remove if there's truly no local data either
    // Missing readable data is not evidence that the original can be discarded.
    // Allow retry in this session by removing from the processed set
    processedRevocations.delete(shareId);
    emit(Events.SHARE_REVOKED, { shareId, wasActive, recoveryFailed: true });
    return { success: false };
  }

  // Create recovered map with regenerated IDs
  try {
    const recoveredDocName = t("share.recovery.copyName", { title });
    // Shared-map recovery requires a new online owner/key for its attachments.
    // A local root may precede the online root in a dual-vault session.
    const rootFolder = VaultStorage.getIndex()?.folders?.find(f => f.parentId === null && f._storage !== 'local');
    const folderId = rootFolder?.id || null;
    const folderName = rootFolder?.name || t("vault.myMaps");

    // Full backups keep IDs so fragments, execution entries and internal links
    // stay connected. IDs are scoped by the new document, as with vault restore.
    if (!crdtState) regenerateIdsAndStripFlags(treeData);
    const fullRefs = crdtState ? [...backupAttachmentReferences(crdtState).keys()]
      .filter(ref => ref.toLowerCase().startsWith(`${shareId.toLowerCase()}/`)) : [];
    const extraBlobIds = fullRefs.map(ref => ref.split('/')[1]);

    // Only data and userSetProjectDates are read by handleDocOpen;
    // view state (zoom, pan, current view) is restored from localStorage
    const mapState = {
      data: treeData,
      userSetProjectDates: false,
    };

    // Registered with its owner and key before the vault lists it, so before
    // attachment references are imported (createDoc; `recoveredFromShareId`
    // goes as recovered_from: recovering is never refused for quota, the
    // copy may take this account over quota and the user then frees space
    // in the vault). Never confirm recovery or remove the old key/cache
    // until the copy is durable.
    const { docId: newDocId } = await VaultStorage.createMap(
      recoveredDocName,
      folderId,
      mapState,
      { recoveredFromShareId: shareId, storage: 'online' },
    );

    // Migrate attachments: rewrap each referenced blob's BEK under the new
    // doc's DEK, register a `doc_blob_refs` row pointing at the same on-disk
    // file, and rewrite `attachment:OLD/...` URIs in the tree to `NEW/...`.
    // Must run BEFORE confirmRecovery: the import endpoint requires an
    // in-flight recovery claim, which confirmRecovery closes. The treeData
    // mutation flows into the doc when it's first opened (createMap stored
    // it as pending initial state by reference, not by deep copy).
    let attachmentMigration = null;
    try {
      const oldDekRaw = await getShareDEK(shareId);
      const newDekRaw = await VaultStorage.getDocDEK(newDocId);
      if (oldDekRaw && newDekRaw) {
        attachmentMigration = await migrateAttachmentsForRecovery(
          shareId,
          newDocId,
          oldDekRaw,
          newDekRaw,
          treeData,
          extraBlobIds,
        );
      } else {
        logWarn("Skipping blob migration — DEK unavailable", { hasOld: !!oldDekRaw, hasNew: !!newDekRaw });
        attachmentMigration = { imported: 0, total: 0, dekUnavailable: true };
      }
    } catch (e) {
      // Retain the partial tree copy, but do not confirm or clean the source.
      logError("Blob migration during recovery incomplete:", e);
      attachmentMigration = { imported: 0, total: 0, error: true };
    }

    const recoveryDek = await VaultStorage.getDocDEK(newDocId);
    try {
      let recoveredState = crdtState;
      if (crdtState && attachmentMigration?.imported === attachmentMigration?.total) {
        const refs = new Map(fullRefs.map(ref => [ref, `attachment:${newDocId}/${ref.split('/')[1]}`]));
        recoveredState = remapBackupLinks(crdtState, new Map([[shareId, newDocId]]), refs);
      }
      await persistRecoveryCopy(newDocId, treeData, recoveryDek, recoveredState);
    }
    finally { recoveryDek.fill(0); }
    if (!completeSource || !attachmentMigration || attachmentMigration.error || attachmentMigration.dekUnavailable ||
        attachmentMigration.imported < attachmentMigration.total) {
      throw new Error('Some attachments could not be recovered. The original snapshot and keys are retained for retry.');
    }

    // Copy view state (zoom, pan, current view) from the old share to the recovered doc
    const oldViewState = getDocState(shareId);
    if (oldViewState) {
      saveDocState(newDocId, oldViewState);
      logDebug("Copied view state from share to recovered doc");
    }

    // Migrate any stubs pointing at the revoked share to the recovered doc.
    // Fast path: vault entry's linkedIntoDocs lists the user's other docs
    // that link this one. For the currently-loaded main doc (if any), we
    // rewrite stubs now; for others, queue a pending migration on their
    // vault entry to be applied on next open.
    try {
      const linkers = VaultStorage.getLinkers?.(shareId) || [];
      if (linkers.length > 0) {
        logInfo(`Migrating stubs for ${shareId} → ${newDocId} in ${linkers.length} host doc(s)`);
        const mainDocId = callbacks?.getDocManager?.()?.docId || null;
        const queuePromises = [];
        for (const hostDocId of linkers) {
          if (hostDocId === mainDocId) {
            // Live rewrite handled by SHARE_REVOKED listener in crdt-bridge.
            continue;
          }
          queuePromises.push(
            VaultStorage.queuePendingStubMigration?.(hostDocId, shareId, newDocId).catch((err) => {
              logWarn("Failed to queue stub migration:", hostDocId, err);
            })
          );
        }
        await Promise.all(queuePromises);
      }
    } catch (err) {
      logWarn("Stub-migration bookkeeping failed (revocation proceeds):", err);
    }

    // NOW remove from VaultStorage - recovery succeeded, DEK no longer needed
    await VaultStorage.deleteDoc(shareId);

    // Clean up the old share's local cache. Through the storage adapter, not
    // a raw deleteDatabase: when the revoked map is still open, its persistence
    // must stand down first, or its next save hits the closing connection and
    // blocks all editing ("Saving stopped") — and the recovered map never opens.
    deleteDocCache(shareId).catch((e) => logDebug("Old share cache cleanup skipped:", e?.message || e));

    // M11: Confirm recovery to server with retry (so it can clean up snapshot when all users confirm)
    if (snapshotData) {
      confirmRecoveryWithRetry(shareId, newDocId);
    }

    emit(Events.SHARE_REVOKED, {
      shareId,
      recoveredDocId: newDocId,
      title: recoveredDocName,
      // The map's title before " (recovered)" was added (that suffix is
      // translated, so listeners can't strip it reliably).
      originalTitle: title,
      folderName,
      wasActive,
      recoverySource,
    });

    // Clean up recovery DEK cache
    recoveryDEKCache.delete(shareId);

    return { success: true, recoveredDocId: newDocId, recoverySource };
  } catch (error) {
    logError("Failed to create recovered map:", error);
    notify({ code: NOTICE.SHARE_RECOVERY_INCOMPLETE, level: 'warning', message: t('share.recovery.incomplete'),
      data: { mapId: shareId }, duration: 10000 });
    // DON'T remove from VaultStorage on failure - user can retry recovery on next login
    // Allow retry in this session by removing from the processed set
    processedRevocations.delete(shareId);
    emit(Events.SHARE_REVOKED, { shareId, wasActive, recoveryFailed: true });
    // Clean up recovery DEK cache even on failure
    recoveryDEKCache.delete(shareId);
    return { success: false };
  }
}

/**
 * Recover data from a revoked share
 * @deprecated Use performShareRevocation instead - this is kept for backward compatibility
 * @param {string} shareId - The share ID
 * @param {Object} shareInfo - Share metadata from vault storage
 * @returns {Promise<boolean>} - True if recovery succeeded
 */
export async function recoverRevokedShare(shareId, shareInfo) {
  const result = await performShareRevocation(shareId, shareInfo?.title, {
    fromLegacy: true,
  });
  // Return false if already processed to prevent duplicate alerts
  return result.success && !result.alreadyProcessed;
}

/**
 * Check if a share has been revoked (or is currently being revoked) in this session.
 * Used by handleDekRotated to avoid reconnecting to a revoked share.
 * @param {string} shareId
 * @returns {boolean}
 */
export function isShareRevoked(shareId) {
  return processedRevocations.has(shareId);
}

/**
 * Clear processed revocations set (used in testing or when resetting state)
 */
export function clearProcessedRevocations() {
  processedRevocations.clear();
  recoveryDEKCache.clear();
}

/** Stop the cleanup interval (call on logout to prevent timer leaks in SPA). */
export function destroy() {
  if (_cleanupIntervalId) {
    clearInterval(_cleanupIntervalId);
    _cleanupIntervalId = null;
  }
  processedRevocations.clear();
  recoveryDEKCache.clear();
}
